import React, { Suspense } from 'react';
import { Box, CircularProgress } from '@mui/material';
import GoogleAnalytics from './components/GoogleAnalytics';
import { SHOW_QA } from './config/featureFlags';
import Navbar from './components/Navbar';
import Home from './pages/Home';
import RequiredLanguageSelectionDialog from './components/RequiredLanguageSelectionDialog';
import LanguageSyncToast from './components/LanguageSyncToast';
import DeferredCookieConsent from './components/DeferredCookieConsent';

import { Routes, Route, Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { RequireAdmin, RequireAuth, VerifiedRouteGate } from './routes/guards';
import Seo from './components/Seo';
import { PAGE_META, buildExamOverviewMeta, buildMockExamsMeta, buildPublicDialogueVideoMeta } from './seo/pageMeta';
import ScrollToTop from './components/ScrollToTop';
import BootRouteBoundary from './bootstrap/BootRouteBoundary';
import { useLanguage } from './context/LanguageContext';
import {
  buildChapterBasePath,
  buildChapterExamplesPath,
  buildChapterTopicsPath,
  buildChapterVideoPath,
  buildExamOverviewPath,
  buildLegacyChapterBasePath,
  buildMockExamsPath,
  buildPracticePath,
  buildPracticeSectionPath,
  buildPracticeSectionViewPath,
  buildPracticeTopicPath,
  buildTopicsBasePath,
  buildTopicsDiscussionPath,
  buildA1SituationPath,
  CHAPTER_TRACKS,
  getPracticeSectionForVocabTopic,
  LANGUAGES,
  resolveStoredChapterTrack,
} from './utils/contentRoutes';
import LegacyChapterRedirect from './pages/Practice/LegacyChapterRedirect';
import { A1_SITUATIONS } from './pages/Practice/practiceConfig';
import { MobileContentsProvider } from './pages/Practice/MobileContentsContext';

const TermsAndConditions = React.lazy(() => import('./pages/TermsAndConditions'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const FIDEExamOverview = React.lazy(() => import('./pages/FIDEExamOverview'));
const FIDEExampleVideos = React.lazy(() => import('./pages/FIDEExampleVideos'));
const TopicVideoPage = React.lazy(() => import('./pages/TopicVideoPage'));
const NotFound = React.lazy(() => import('./pages/NotFound'));
const AuthLogin = React.lazy(() => import('./pages/AuthLogin'));
const AuthSignUp = React.lazy(() => import('./pages/AuthSignUp'));
const AuthVerify = React.lazy(() => import('./pages/AuthVerify'));
const ExamFlow = React.lazy(() => import('./pages/MockExam/ExamFlow'));
const ListeningFlow = React.lazy(() => import('./pages/MockExam/ListeningFlow'));
const PracticeRouter = React.lazy(() => import('./pages/MockExam/PracticeRouter'));
const MockExams = React.lazy(() => import('./pages/MockExam/MockExams'));
const Pricing = React.lazy(() => import('./pages/Pricing'));
const ExamTips = React.lazy(() => import('./pages/ExamTips'));
const Practice = React.lazy(() => import('./pages/Flashcards'));
const ContactUs = React.lazy(() => import('./pages/ContactUs'));
const FideVocab = React.lazy(() => import('./pages/FideVocab'));
const ChapterTopicsPage = React.lazy(() => import('./pages/ChapterTopicsPage'));
const ChapterVocabPage = React.lazy(() => import('./pages/ChapterVocabPage'));
const ChapterFlashcardsPage = React.lazy(() => import('./pages/ChapterFlashcardsPage'));
const PracticeSectionResource = React.lazy(() => import('./pages/PracticeSectionResource'));
const Chapters = React.lazy(() => import('./pages/Chapters'));
const Progress = React.lazy(() => import('./pages/Progress'));
const PracticeMap = React.lazy(() => import('./pages/Practice/PracticeMap'));
const ListeningPractice = React.lazy(() => import('./pages/Practice/ListeningPractice'));
const PracticeSectionPage = React.lazy(() => import('./pages/Practice/PracticeLayout'));
const PracticeCatalogLayout = React.lazy(() =>
  import('./pages/Practice/PracticeLayout').then((module) => ({ default: module.PracticeCatalogLayout })),
);
const PracticeResourceLayout = React.lazy(() =>
  import('./pages/Practice/PracticeLayout').then((module) => ({ default: module.PracticeResourceLayout })),
);
const Account = React.lazy(() => import('./pages/Account'));
const QaAskQuestion = React.lazy(() => import('./pages/QaAskQuestion'));
const QaMyQuestions = React.lazy(() => import('./pages/QaMyQuestions'));
const QaQuestionList = React.lazy(() => import('./pages/QaQuestionList'));
const QaQuestionPage = React.lazy(() => import('./pages/QaQuestionPage'));
const QaModerationQueue = React.lazy(() => import('./pages/admin/QaModerationQueue'));
const BlogIndex = React.lazy(() => import('./pages/blog/BlogIndex'));
const BlogPost = React.lazy(() => import('./pages/blog/BlogPost'));

const RouteFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
    <CircularProgress />
  </Box>
);

const renderLazyPage = (PageComponent) => (
  <Suspense fallback={<RouteFallback />}>
    <PageComponent />
  </Suspense>
);

const DialogueVideoRoute = () => {
  const location = useLocation();
  const { scenarioId } = useParams();
  const lang = location.pathname.startsWith('/de/') ? 'de' : 'fr';
  const seoMeta = buildPublicDialogueVideoMeta({ lang, scenarioId, pathOverride: location.pathname });

  return (
    <>
      {seoMeta ? <Seo {...seoMeta} /> : null}
      <Suspense fallback={<RouteFallback />}>
        <TopicVideoPage routeSeoHandled={Boolean(seoMeta)} />
      </Suspense>
    </>
  );
};

const LegacyExamOverviewSeoRedirect = () => {
  const { language, loading } = useLanguage();

  if (loading) {
    return <RouteFallback />;
  }

  const targetPath = buildExamOverviewPath(language);

  return (
    <>
      <Seo {...buildExamOverviewMeta(language)} path="/fide-exam-overview" canonicalUrl={targetPath} noindex />
      <Navigate to={targetPath} replace />
    </>
  );
};

const LegacyMockExamsSeoRedirect = () => {
  const { examType } = useParams();
  const { language, loading } = useLanguage();

  if (loading) {
    return <RouteFallback />;
  }

  const targetPath = buildMockExamsPath(language, examType);

  return (
    <>
      <Seo {...buildMockExamsMeta(language)} path="/mock-exams" canonicalUrl={targetPath} noindex />
      <Navigate to={targetPath} replace />
    </>
  );
};

const ChapterExamplesEntry = ({ language }) => {
  const location = useLocation();
  return (
    <Navigate
      to={`${buildChapterExamplesPath(language, resolveStoredChapterTrack())}${location.search}${location.hash}`}
      replace
    />
  );
};

/** Former nested catalog URLs forward to their shorter canonical section URLs. */
const LegacyPracticeExamplesRedirect = ({ language, track }) => {
  const location = useLocation();
  const target =
    track === 'dialogue'
      ? buildPracticeSectionPath(language, 'a2', 'telephone-roleplay')
      : buildChapterExamplesPath(language, track);
  return <Navigate to={`${target}${location.search}${location.hash}`} replace />;
};

/**
 * The old track-based topic URLs (`/fide-practice/a2/telephone/{topics|vocab|flashcards}/:id`) forward
 * to the new scheme: catalog topics to the topic-centric shape, and the guided A2-section topics
 * (17–19) to that section's own view page. `view` is 'dialogue' for the topic-detail route.
 */
const LegacyTopicViewRedirect = ({ language, view }) => {
  const location = useLocation();
  const { topicId } = useParams();
  const guided = getPracticeSectionForVocabTopic(topicId);
  const target = guided
    ? buildPracticeSectionViewPath(language, guided[0], guided[1], view === 'dialogue' ? 'vocab' : view)
    : buildPracticeTopicPath(language, topicId, view);
  return <Navigate to={`${target}${location.hash}`} replace state={location.state} />;
};

/** A guided section's `/…/topics` view is just the section base (which renders its topics). */
const SectionTopicsRedirect = () => {
  const location = useLocation();
  const { level, section } = useParams();
  const language = location.pathname.startsWith('/de/') ? 'de' : 'fr';
  return <Navigate to={`${buildPracticeSectionPath(language, level, section)}${location.hash}`} replace />;
};

const A1SituationRoute = () => {
  const { situationId } = useParams();
  const location = useLocation();
  const language = location.pathname.startsWith('/de/') ? 'de' : 'fr';
  const isValid = A1_SITUATIONS.some((situation) => situation.id === Number(situationId));
  return isValid ? renderLazyPage(PracticeSectionPage) : <Navigate to={buildA1SituationPath(language)} replace />;
};

const LegacyTopicsVideoRedirect = ({ language }) => {
  const { scenarioId } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const extraParams = new URLSearchParams(searchParams);
  extraParams.delete('topicId');

  const search = extraParams.toString();
  const nextPath = buildChapterVideoPath(language, 'dialogue', scenarioId);
  return (
    <Navigate to={`${search ? `${nextPath}?${search}` : nextPath}${location.hash}`} replace state={location.state} />
  );
};

function App() {
  const location = useLocation();
  const overlayBackgroundLocation = location.state?.overlayBackgroundLocation;
  const routesLocation = overlayBackgroundLocation || location;

  return (
    <MobileContentsProvider>
      <GoogleAnalytics />
      <ScrollToTop />
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Navbar />
        <RequiredLanguageSelectionDialog />
        <LanguageSyncToast />
        <VerifiedRouteGate gatedPathname={routesLocation.pathname}>
          <BootRouteBoundary pathnameOverride={routesLocation.pathname}>
            <Routes location={routesLocation}>
              <Route path="/" element={<Home />} />
              <Route path="/fr" element={<Navigate to="/" replace />} />
              <Route path="/de" element={<Navigate to="/" replace />} />
              {/* Auth routes */}
              <Route path="/auth/login" element={renderLazyPage(AuthLogin)} />
              <Route path="/auth/signup" element={renderLazyPage(AuthSignUp)} />
              <Route path="/auth/verify" element={renderLazyPage(AuthVerify)} />
              <Route path="/terms_and_conditions" element={renderLazyPage(TermsAndConditions)} />
              <Route path="/privacy_policy" element={renderLazyPage(PrivacyPolicy)} />
              <Route element={renderLazyPage(PracticeResourceLayout)}>
                <Route path="/fr/fide-exam-overview" element={renderLazyPage(FIDEExamOverview)} />
                <Route path="/de/fide-exam-overview" element={renderLazyPage(FIDEExamOverview)} />
              </Route>
              <Route path="/fide-exam-overview" element={<LegacyExamOverviewSeoRedirect />} />
              <Route element={renderLazyPage(PracticeResourceLayout)}>
                <Route path="/fr/mock-exams" element={renderLazyPage(MockExams)} />
                <Route path="/de/mock-exams" element={renderLazyPage(MockExams)} />
                {/* The Oral/Listening switch is a URL, not just state — Back returns to the tab you
                    came from, and either tab can be linked or bookmarked. */}
                <Route path="/fr/mock-exams/:examType" element={renderLazyPage(MockExams)} />
                <Route path="/de/mock-exams/:examType" element={renderLazyPage(MockExams)} />
              </Route>
              <Route path="/mock-exams" element={<LegacyMockExamsSeoRedirect />} />
              <Route path="/mock-exams/:examType" element={<LegacyMockExamsSeoRedirect />} />
              <Route path="/pricing" element={renderLazyPage(Pricing)} />
              <Route path="/subscription/success" element={renderLazyPage(Pricing)} />
              <Route path="/mock-exam/flow" element={<RequireAuth>{renderLazyPage(ExamFlow)}</RequireAuth>} />
              <Route path="/mock-exam/review/:examId" element={<RequireAuth>{renderLazyPage(ExamFlow)}</RequireAuth>} />
              <Route
                path="/mock-exam/listening-flow"
                element={<RequireAuth>{renderLazyPage(ListeningFlow)}</RequireAuth>}
              />
              <Route
                path="/mock-exam/listening-review/:reviewExamId"
                element={<RequireAuth>{renderLazyPage(ListeningFlow)}</RequireAuth>}
              />
              {/* A section's practice is an exam flow, so like the flows above it runs without the sidebar. */}
              <Route
                path="/journey/practice/:type/:level/:sectionId"
                element={<RequireAuth>{renderLazyPage(PracticeRouter)}</RequireAuth>}
              />
              <Route element={renderLazyPage(PracticeResourceLayout)}>
                <Route path="/fr/exam-tips" element={renderLazyPage(ExamTips)} />
                <Route path="/fr/exam-tips/:tipId" element={renderLazyPage(ExamTips)} />
                <Route path="/de/exam-tips" element={renderLazyPage(ExamTips)} />
                <Route path="/de/exam-tips/:tipId" element={renderLazyPage(ExamTips)} />
              </Route>
              {/* The `/discussion` variant is retired — one topic catalog serves both tracks now. */}
              <Route
                path="/fr/fide-test-topics/discussion"
                element={<Navigate to={buildTopicsDiscussionPath('fr')} replace />}
              />
              <Route
                path="/de/fide-test-topics/discussion"
                element={<Navigate to={buildTopicsDiscussionPath('de')} replace />}
              />
              <Route
                path="/fr/fide-test-topics/video/:scenarioId"
                element={<LegacyTopicsVideoRedirect language="fr" />}
              />
              <Route
                path="/de/fide-test-topics/video/:scenarioId"
                element={<LegacyTopicsVideoRedirect language="de" />}
              />
              {/* Indexed material roots keep the topic picker, filtered to their material. */}
              <Route
                path="/fr/fide-vocabulary"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <PracticeMap fixedMaterial="vocab" />
                  </Suspense>
                }
              />
              <Route
                path="/de/fide-vocabulary"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <PracticeMap fixedMaterial="vocab" />
                  </Suspense>
                }
              />
              <Route
                path="/fr/fide-flashcards"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <PracticeMap fixedMaterial="flashcards" />
                  </Suspense>
                }
              />
              <Route
                path="/de/fide-flashcards"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <PracticeMap fixedMaterial="flashcards" />
                  </Suspense>
                }
              />
              <Route path="/fr/fide-vocab" element={<Navigate to="/fr/fide-vocabulary" replace />} />
              <Route path="/de/fide-vocab" element={<Navigate to="/de/fide-vocabulary" replace />} />
              {/* The material libraries render inside the practice sidebar. */}
              <Route element={renderLazyPage(PracticeResourceLayout)}>
                <Route path="/fr/fide-test-example-videos" element={renderLazyPage(FIDEExampleVideos)} />
                <Route path="/fr/fide-practice/topics/all/vocab" element={renderLazyPage(FideVocab)} />
                <Route path="/de/fide-practice/topics/all/vocab" element={renderLazyPage(FideVocab)} />
                <Route path="/fr/fide-practice/topics/all/flashcards" element={renderLazyPage(Practice)} />
                <Route path="/de/fide-practice/topics/all/flashcards" element={renderLazyPage(Practice)} />
              </Route>
              {/* The single-video player stays full-width, outside the sidebar. */}
              <Route path="/fr/fide-test-example-videos/:youtubeId" element={renderLazyPage(FIDEExampleVideos)} />
              <Route path="/fr/fide-test-examples" element={<ChapterExamplesEntry language="fr" />} />
              <Route path="/de/fide-test-examples" element={<ChapterExamplesEntry language="de" />} />
              {/* The map's two tabs are two URLs: the level journey and the topic catalog.
                `/{lang}/fide-test-topics` is the older, indexed one, so it stays a real page
                rather than a `?view=` on the map. Same component either way. */}
              {LANGUAGES.map((lang) => (
                <Route
                  key={`practice-map-${lang}`}
                  path={buildPracticePath(lang)}
                  element={renderLazyPage(PracticeMap)}
                />
              ))}
              {LANGUAGES.map((lang) => (
                <Route
                  key={`practice-topics-map-${lang}`}
                  path={buildTopicsBasePath(lang)}
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <PracticeMap fixedMaterial="dialogue" />
                    </Suspense>
                  }
                />
              ))}
              {LANGUAGES.map((lang) => (
                <Route
                  key={`practice-discussion-map-${lang}`}
                  path={buildTopicsDiscussionPath(lang)}
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <PracticeMap fixedMaterial="discussion" />
                    </Suspense>
                  }
                />
              ))}
              {LANGUAGES.map((lang) => (
                <Route
                  key={`practice-section-${lang}`}
                  path={`${buildPracticePath(lang)}/:level/:section`}
                  element={renderLazyPage(PracticeSectionPage)}
                />
              ))}
              {LANGUAGES.map((lang) => (
                <Route
                  key={`practice-listening-${lang}`}
                  path={`${buildPracticePath(lang)}/listening/:level/:setId?`}
                  element={renderLazyPage(ListeningPractice)}
                />
              ))}
              {/* A1's section is one page per situation. All-static segments outrank the
                `:level/:section/{vocab,flashcards,topics}` routes below, and A1 has no
                vocab views, so the two shapes never collide. */}
              {LANGUAGES.map((lang) => (
                <Route
                  key={`practice-a1-situation-${lang}`}
                  path={`${buildPracticePath(lang)}/a1/dialogue/:situationId`}
                  element={<A1SituationRoute />}
                />
              ))}

              {/* Topic-centric routes: the clean `/fide-practice/topics/{id}/{view}` shape the Topic
                catalog uses. ChapterTopicsPage clamps a view the topic doesn't have. */}
              {LANGUAGES.map((lang) => (
                <Route key={`practice-topic-${lang}`} element={renderLazyPage(PracticeCatalogLayout)}>
                  <Route
                    path={`${buildPracticePath(lang)}/topics/:topicId/dialogue`}
                    element={renderLazyPage(ChapterTopicsPage)}
                  />
                  <Route
                    path={`${buildPracticePath(lang)}/topics/:topicId/discussion`}
                    element={renderLazyPage(ChapterTopicsPage)}
                  />
                  <Route
                    path={`${buildPracticePath(lang)}/topics/:topicId/vocab`}
                    element={renderLazyPage(ChapterVocabPage)}
                  />
                  <Route
                    path={`${buildPracticePath(lang)}/topics/:topicId/flashcards`}
                    element={renderLazyPage(ChapterFlashcardsPage)}
                  />
                </Route>
              ))}

              {/* Guided A2 sections: base stays canonical; vocab/flashcards are section-scoped views.
                PracticeSectionResource renders its own sidebar, so it is not nested in the layout. */}
              {LANGUAGES.map((lang) => (
                <React.Fragment key={`practice-section-view-${lang}`}>
                  <Route
                    path={`${buildPracticePath(lang)}/:level/:section/vocab`}
                    element={
                      <Suspense fallback={<RouteFallback />}>
                        <PracticeSectionResource view="vocab" />
                      </Suspense>
                    }
                  />
                  <Route
                    path={`${buildPracticePath(lang)}/:level/:section/flashcards`}
                    element={
                      <Suspense fallback={<RouteFallback />}>
                        <PracticeSectionResource view="flashcards" />
                      </Suspense>
                    }
                  />
                  <Route
                    path={`${buildPracticePath(lang)}/:level/:section/topics`}
                    element={<SectionTopicsRedirect />}
                  />
                </React.Fragment>
              ))}

              {/* The catalogs + the Level rail's (B1 → Discussion) topic pages, inside the practice
                sidebar. Dialogue's old track URLs redirect to the new topic scheme. */}
              {LANGUAGES.map((lang) => (
                <Route key={`practice-catalog-${lang}`} element={renderLazyPage(PracticeCatalogLayout)}>
                  {CHAPTER_TRACKS.map((track) => (
                    <React.Fragment key={`${lang}-${track}`}>
                      {/* The dialogue catalog grid is retired: A2 › Telephone routes
                        straight to the guided roleplay section and never shows the
                        old topic grid. Discussion keeps its catalog (it is the B1 section). */}
                      <Route
                        path={buildChapterExamplesPath(lang, track)}
                        element={
                          track === 'dialogue' ? (
                            <Navigate to={buildPracticeSectionPath(lang, 'a2', 'telephone-roleplay')} replace />
                          ) : (
                            renderLazyPage(Chapters)
                          )
                        }
                      />
                      <Route
                        path={`${buildChapterBasePath(lang, track)}/fide-test-examples`}
                        element={<LegacyPracticeExamplesRedirect language={lang} track={track} />}
                      />
                      <Route
                        path={buildChapterTopicsPath(lang, track)}
                        element={
                          track === 'dialogue' ? (
                            <Navigate to={buildPracticeSectionPath(lang, 'a2', 'telephone-roleplay')} replace />
                          ) : (
                            renderLazyPage(ChapterTopicsPage)
                          )
                        }
                      />
                      <Route
                        path={`${buildChapterTopicsPath(lang, track)}/:topicId`}
                        element={
                          track === 'discussion' ? (
                            renderLazyPage(ChapterTopicsPage)
                          ) : (
                            <LegacyTopicViewRedirect language={lang} view="dialogue" />
                          )
                        }
                      />
                      <Route
                        path={`${buildChapterBasePath(lang, track)}/vocab/:topicId`}
                        element={
                          track === 'discussion' ? (
                            renderLazyPage(ChapterVocabPage)
                          ) : (
                            <LegacyTopicViewRedirect language={lang} view="vocab" />
                          )
                        }
                      />
                      <Route
                        path={`${buildChapterBasePath(lang, track)}/flashcards/:topicId`}
                        element={
                          track === 'discussion' ? (
                            renderLazyPage(ChapterFlashcardsPage)
                          ) : (
                            <LegacyTopicViewRedirect language={lang} view="flashcards" />
                          )
                        }
                      />
                    </React.Fragment>
                  ))}
                </Route>
              ))}

              {/* Video stays outside the sidebar — it also renders as a modal overlay. */}
              {LANGUAGES.map((lang) => (
                <Route
                  key={`practice-video-${lang}`}
                  path={`${buildChapterBasePath(lang, 'dialogue')}/video/:scenarioId`}
                  element={<DialogueVideoRoute />}
                />
              ))}

              {/* SEO shims: the old, indexed catalog URLs forward to their new homes. */}
              {LANGUAGES.flatMap((lang) =>
                CHAPTER_TRACKS.flatMap((track) => {
                  const legacyBase = buildLegacyChapterBasePath(lang, track);
                  return [
                    <Route
                      key={`legacy-${lang}-${track}-examples`}
                      path={`${legacyBase}/fide-test-examples`}
                      element={<LegacyChapterRedirect language={lang} track={track} kind="examples" />}
                    />,
                    <Route
                      key={`legacy-${lang}-${track}-topics`}
                      path={`${legacyBase}/topics`}
                      element={<LegacyChapterRedirect language={lang} track={track} kind="topicsList" />}
                    />,
                    <Route
                      key={`legacy-${lang}-${track}-topic`}
                      path={`${legacyBase}/topics/:topicId`}
                      element={<LegacyChapterRedirect language={lang} track={track} kind="topic" />}
                    />,
                    <Route
                      key={`legacy-${lang}-${track}-vocab`}
                      path={`${legacyBase}/vocab/:topicId`}
                      element={<LegacyChapterRedirect language={lang} track={track} kind="vocab" />}
                    />,
                    <Route
                      key={`legacy-${lang}-${track}-flashcards`}
                      path={`${legacyBase}/flashcards/:topicId`}
                      element={<LegacyChapterRedirect language={lang} track={track} kind="flashcards" />}
                    />,
                  ];
                }),
              )}
              {LANGUAGES.map((lang) => (
                <Route
                  key={`legacy-video-${lang}`}
                  path={`${buildLegacyChapterBasePath(lang, 'dialogue')}/video/:scenarioId`}
                  element={<LegacyTopicsVideoRedirect language={lang} />}
                />
              ))}

              <Route path="/blog" element={renderLazyPage(BlogIndex)} />
              <Route path="/blog/podcast/:postId" element={renderLazyPage(BlogPost)} />
              <Route path="/blog/:postId" element={renderLazyPage(BlogPost)} />
              {/* <Route path="/progress" element={renderLazyPage(Progress)} /> */}
              <Route path="/account" element={<RequireAuth>{renderLazyPage(Account)}</RequireAuth>} />
              {SHOW_QA && (
                <>
                  <Route path="/questions" element={renderLazyPage(QaQuestionList)} />
                  <Route path="/questions/new" element={<RequireAuth>{renderLazyPage(QaAskQuestion)}</RequireAuth>} />
                  <Route path="/questions/mine" element={<RequireAuth>{renderLazyPage(QaMyQuestions)}</RequireAuth>} />
                  <Route path="/questions/:slug" element={renderLazyPage(QaQuestionPage)} />
                  <Route
                    path="/admin/qa/moderation"
                    element={<RequireAdmin>{renderLazyPage(QaModerationQueue)}</RequireAdmin>}
                  />
                </>
              )}
              <Route path="/contact-us" element={renderLazyPage(ContactUs)} />
              <Route path="/404" element={renderLazyPage(NotFound)} />
              <Route path="*" element={renderLazyPage(NotFound)} />
            </Routes>
          </BootRouteBoundary>
        </VerifiedRouteGate>
        {overlayBackgroundLocation ? (
          <BootRouteBoundary pathnameOverride={location.pathname}>
            <Routes>
              <Route
                path={`${buildChapterBasePath('fr', 'dialogue')}/video/:scenarioId`}
                element={<DialogueVideoRoute />}
              />
              <Route
                path={`${buildChapterBasePath('de', 'dialogue')}/video/:scenarioId`}
                element={<DialogueVideoRoute />}
              />
            </Routes>
          </BootRouteBoundary>
        ) : null}
      </Box>
      <DeferredCookieConsent />
    </MobileContentsProvider>
  );
}

export default App;
